import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import StatusOverview from '../components/public/StatusOverview';
import ServiceStatus from '../components/public/ServiceStatus';
import UptimeChart from '../components/public/UptimeChart';
import IncidentTimeline from '../components/public/IncidentTimeline';
import SubscribeNotifications from '../components/public/SubscribeNotifications';
import { statusApi } from '../services/api';
import { StatusOverview as StatusOverviewType, Incident, UptimeStats } from '../types';

const PublicStatus: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [statusData, setStatusData] = useState<StatusOverviewType | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [uptimeData, setUptimeData] = useState<UptimeStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSubscribe, setShowSubscribe] = useState(false);

  // Check if we're on the subscribe page
  const isSubscribePage = location.pathname === '/subscribe';

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch all data in parallel
        const [status, incidentsData, uptime] = await Promise.all([
          statusApi.getStatus().catch(() => null),
          statusApi.getIncidents({ limit: 5 }).catch(() => ({ data: [] })),
          statusApi.getUptime().catch(() => null),
        ]);

        setStatusData(status);
        setIncidents(incidentsData.data || []);
        setUptimeData(uptime);
      } catch (error) {
        console.error('Failed to fetch status data:', error);
        // Set mock data for development
        setStatusData({
          status: 'operational',
          message: 'All systems are operating normally.',
          services: [
            { name: 'Web Application', status: 'operational', responseTimeMs: 45, lastChecked: new Date().toISOString() },
            { name: 'API', status: 'operational', responseTimeMs: 23, lastChecked: new Date().toISOString() },
            { name: 'Database', status: 'operational', responseTimeMs: 8, lastChecked: new Date().toISOString() },
            { name: 'WebSocket', status: 'operational', responseTimeMs: 12, lastChecked: new Date().toISOString() },
            { name: 'File Storage', status: 'operational', responseTimeMs: 35, lastChecked: new Date().toISOString() },
          ],
          updatedAt: new Date().toISOString(),
        });
        setUptimeData({
          daily: [],
          last30Days: 99.98,
          last90Days: 99.95,
          lastYear: 99.92,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Refresh data every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Show subscribe modal if on subscribe page
  useEffect(() => {
    if (isSubscribePage) {
      setShowSubscribe(true);
    }
  }, [isSubscribePage]);

  const handleCloseSubscribe = () => {
    setShowSubscribe(false);
    if (isSubscribePage) {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header showSubscribe={!showSubscribe} />

      <main className="flex-1 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {/* Subscribe modal/section */}
          {showSubscribe ? (
            <SubscribeNotifications onClose={handleCloseSubscribe} />
          ) : (
            <>
              {/* Status overview */}
              <StatusOverview data={statusData} isLoading={isLoading} />

              {/* Services status */}
              <ServiceStatus
                services={statusData?.services || []}
                isLoading={isLoading}
              />

              {/* Uptime chart */}
              <UptimeChart data={uptimeData} isLoading={isLoading} />

              {/* Recent incidents */}
              <IncidentTimeline
                incidents={incidents}
                isLoading={isLoading}
                onViewMore={() => navigate('/incidents')}
              />
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PublicStatus;
